import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Button,
  Segmented,
  SegmentedButton,
} from 'konsta/react'
import {
  Users,
  DoorOpen,
  DollarSign,
  ArrowLeftRight,
  AlertTriangle,
  ShieldAlert,
  Banknote,
  MessageSquareWarning,
  Bell,
  Settings,
  Clock,
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { formatCurrency } from '@/lib/utils'

const tabs = ['即時', '今日', '本週', '本月'] as const
type Tab = (typeof tabs)[number]

const revenueData = [
  { name: '週一', revenue: 3200 },
  { name: '週二', revenue: 4100 },
  { name: '週三', revenue: 3800 },
  { name: '週四', revenue: 4520 },
  { name: '週五', revenue: 5100 },
  { name: '週六', revenue: 6200 },
  { name: '週日', revenue: 5800 },
]

const playerData = [
  { name: '00:00', players: 45 },
  { name: '04:00', players: 22 },
  { name: '08:00', players: 38 },
  { name: '12:00', players: 89 },
  { name: '16:00', players: 120 },
  { name: '20:00', players: 145 },
  { name: '23:00', players: 98 },
]

interface MetricCard {
  label: string
  value: string
  icon: React.ElementType
  color: string
  bgColor: string
}

const metrics: MetricCard[] = [
  {
    label: '在線玩家',
    value: '127',
    icon: Users,
    color: '#007AFF',
    bgColor: 'rgba(0, 122, 255, 0.1)',
  },
  {
    label: '活躍房間',
    value: '8',
    icon: DoorOpen,
    color: '#34C759',
    bgColor: 'rgba(52, 199, 89, 0.1)',
  },
  {
    label: '今日收入',
    value: formatCurrency(452000),
    icon: DollarSign,
    color: '#FF9500',
    bgColor: 'rgba(255, 149, 0, 0.1)',
  },
  {
    label: '今日交易',
    value: '234',
    icon: ArrowLeftRight,
    color: '#AF52DE',
    bgColor: 'rgba(175, 82, 222, 0.1)',
  },
]

interface Alert {
  id: string
  type: 'warning' | 'fraud' | 'withdrawal'
  message: string
  time: string
}

const recentAlerts: Alert[] = [
  {
    id: '1',
    type: 'fraud',
    message: '偵測到可疑的快速交易模式 - 用戶 @alex_k',
    time: '5 分鐘前',
  },
  {
    id: '2',
    type: 'withdrawal',
    message: '大額提款申請 $5,000 待審核',
    time: '12 分鐘前',
  },
  {
    id: '3',
    type: 'warning',
    message: '伺服器 CPU 使用率達 85%',
    time: '30 分鐘前',
  },
  {
    id: '4',
    type: 'withdrawal',
    message: '提款申請 $2,200 待審核 - @lucy_wu',
    time: '1 小時前',
  },
  {
    id: '5',
    type: 'fraud',
    message: '多帳號關聯警報 - 3 組帳號共用 IP',
    time: '2 小時前',
  },
]

const alertIconMap: Record<Alert['type'], React.ElementType> = {
  warning: AlertTriangle,
  fraud: ShieldAlert,
  withdrawal: Banknote,
}

const alertColorMap: Record<Alert['type'], { text: string; bg: string }> = {
  warning: { text: '#FF9500', bg: 'rgba(255, 149, 0, 0.1)' },
  fraud: { text: '#FF3B30', bg: 'rgba(255, 59, 48, 0.1)' },
  withdrawal: { text: '#007AFF', bg: 'rgba(0, 122, 255, 0.1)' },
}

export function AdminDashboardScreen() {
  const [activeTab, setActiveTab] = useState<Tab>('即時')
  const navigate = useNavigate()

  return (
    <div className="pb-4">
      {/* Period Tabs */}
      <Block className="!mt-4 !mb-0">
        <Segmented strong rounded>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {/* Key Metrics Grid */}
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((metric) => {
            const Icon = metric.icon
            return (
              <div
                key={metric.label}
                className="rounded-2xl bg-white p-4 shadow-sm border border-black/5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: metric.bgColor }}
                  >
                    <Icon className="w-4 h-4" style={{ color: metric.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-black">{metric.value}</p>
                <p className="text-xs text-[#8E8E93] mt-0.5">{metric.label}</p>
              </div>
            )
          })}
        </div>
      </Block>

      {/* Revenue Chart */}
      <BlockTitle>收入趨勢</BlockTitle>
      <Block strong inset>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={{ stroke: '#E5E5EA' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `$${(v / 100).toFixed(0)}`}
            />
            <Tooltip
              formatter={(value: number | undefined) => [
                formatCurrency(value ?? 0),
                '收入',
              ]}
              labelStyle={{ color: '#8E8E93' }}
              contentStyle={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#007AFF"
              strokeWidth={2.5}
              dot={{ fill: '#007AFF', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Block>

      {/* Player Activity Chart */}
      <BlockTitle>玩家活躍度</BlockTitle>
      <Block strong inset>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={playerData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={{ stroke: '#E5E5EA' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value: number | undefined) => [
                `${value ?? 0} 人`,
                '在線玩家',
              ]}
              labelStyle={{ color: '#8E8E93' }}
              contentStyle={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            />
            <Bar
              dataKey="players"
              fill="#34C759"
              radius={[6, 6, 0, 0]}
              barSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </Block>

      {/* Recent Alerts */}
      <BlockTitle className="flex items-center justify-between !pr-4">
        <span>近期警報</span>
        <span className="text-xs font-medium text-[#FF3B30]">5 則未讀</span>
      </BlockTitle>
      <List strongIos insetIos>
        {recentAlerts.map((alert) => {
          const Icon = alertIconMap[alert.type]
          const colors = alertColorMap[alert.type]
          return (
            <ListItem
              key={alert.id}
              title={
                <span className="text-sm leading-tight line-clamp-2">
                  {alert.message}
                </span>
              }
              subtitle={
                <span className="flex items-center gap-1 text-[#8E8E93] mt-0.5">
                  <Clock className="w-3 h-3" />
                  {alert.time}
                </span>
              }
              media={
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.bg }}
                >
                  <Icon className="w-5 h-5" style={{ color: colors.text }} />
                </div>
              }
            />
          )
        })}
      </List>

      {/* Quick Actions */}
      <BlockTitle>快速操作</BlockTitle>
      <Block className="!px-4">
        <div className="grid grid-cols-2 gap-3">
          <Button
            large
            tonal
            rounded
            onClick={() => navigate('/admin/withdrawals')}
            className="!h-auto !py-4"
          >
            <span className="flex flex-col items-center gap-2">
              <Banknote className="w-6 h-6" />
              <span className="text-sm font-semibold">審核提款</span>
            </span>
          </Button>
          <Button
            large
            tonal
            rounded
            onClick={() => navigate('/admin/disputes')}
            className="!h-auto !py-4"
          >
            <span className="flex flex-col items-center gap-2">
              <MessageSquareWarning className="w-6 h-6" />
              <span className="text-sm font-semibold">處理爭議</span>
            </span>
          </Button>
          <Button
            large
            tonal
            rounded
            onClick={() => navigate('/admin/fraud')}
            className="!h-auto !py-4"
          >
            <span className="flex flex-col items-center gap-2">
              <Bell className="w-6 h-6" />
              <span className="text-sm font-semibold">查看警報</span>
            </span>
          </Button>
          <Button
            large
            tonal
            rounded
            onClick={() => navigate('/admin/config')}
            className="!h-auto !py-4"
          >
            <span className="flex flex-col items-center gap-2">
              <Settings className="w-6 h-6" />
              <span className="text-sm font-semibold">系統設定</span>
            </span>
          </Button>
        </div>
      </Block>
    </div>
  )
}
